'use client';

import { EPlatform } from '@/utils/enums/platform-enum';
import { useStore } from '@/providers/app-store-provider';
import ClientOnly from '@/components/Responsive/ClientOnly';
import React from 'react';
import dynamic from 'next/dynamic';
import CategoryNavBig from './components/CategoryNavBig/CategoryNavBig';
import type { HomeButtonTrackingParams } from './types';

// ⚠️ 首屏 SSR 必现的板块统一改为静态 import：
// next/dynamic({ ssr: true }) 在客户端走 React.lazy，chunk 未就绪时会被 Suspense 拦截，
// 无 loading 时 promise 冒泡到顶层 page.tsx 的 <Suspense fallback={CriticalSkeleton}> 引发二次全页骨架，
// 加 loading 又会用占位替换 SSR 已渲染的真实 HTML 引发大规模闪动。
// 这些组件本就是首屏必需内容，直接走主 bundle，hydrate 立即可用，无 chunk 挂起。
import CategoryNavShape from './components/CategoryNavShape/CategoryNavShape';
import CategoryNavSmall from './components/CategoryNavSmall/CategoryNavSmall';
import BlogArticle from './components/BlogArticle/BlogArticle';
import BrandImage from './components/BrandImage/BrandImage';
import FivePicSwiper from './components/FivePicSwiper/FivePicSwiper';
import HeroBanner from './components/HomeBanner/HomeBanner';
import MultiSwipe from './components/MultiSwipe/MultiSwipe';
import OneLineTwo from './components/OneLineTwo/OneLineTwo';
import ProductList from './components/ProductList';

// 非本次 SEO 范围的客户端板块继续保留按需加载。
const StyleHub = dynamic(() => import('./components/StyleHub/StyleHub'), {
  ssr: false,
  loading: () => <div className="h-[300px] animate-pulse bg-[#f5f5f5]" />,
});
const WaterFall = dynamic(() => import('./components/WaterFall/WaterFall'), {
  ssr: false,
  loading: () => <div className="h-[400px] animate-pulse bg-[#f5f5f5]" />,
});

interface HomePageBlockItem {
  id?: number;
  component_id?: string;
  jump_target?: any[];
  [key: string]: any;
}

export type HomeComponentMap = Record<string, React.ComponentType<any>>;
export type HomeServerRenderPolicy = 'all' | 'seo-content-only';

const SEO_CONTENT_COMPONENT_IDS = new Set(['blogArticle', 'brandImage']);

interface HomeSectionRendererProps {
  listData: HomePageBlockItem[];
  deviceType: EPlatform;
  onTrack?: (eventParams: any) => void;
  onExpTrack?: (eventParams: any) => void;
  onValidExpTrack?: (eventParams: any) => void;
  onBtnTrack?: (eventParams: HomeButtonTrackingParams) => void;
  homeListData?: HomePageBlockItem[];
  componentMap?: HomeComponentMap;
  serverRenderPolicy?: HomeServerRenderPolicy;
}

export const OLD_COMPONENT_MAP: HomeComponentMap = {
  swipeImage: HeroBanner,
  categoryNavSmall: CategoryNavSmall,
  categoryNavShape: CategoryNavShape,
  categoryNavBig: CategoryNavBig,
  styleHub: StyleHub,
  multiSwipe: MultiSwipe,
  oneLineTwo: OneLineTwo,
  multiSwipeSecond: FivePicSwiper,
  blogArticle: BlogArticle,
  brandImage: BrandImage,
  regularList: ProductList,
  waterFall: WaterFall,
};

export default function HomeSectionRenderer({
  listData,
  deviceType = EPlatform.WEB,
  onTrack,
  onExpTrack,
  onValidExpTrack,
  onBtnTrack,
  homeListData = [],
  componentMap,
  serverRenderPolicy = 'all',
}: HomeSectionRendererProps) {
  const resolvedComponentMap = componentMap ?? OLD_COMPONENT_MAP;
  const isMobile = deviceType === EPlatform.WAP;
  const homeAfterWaterFallStartIndex = useStore((s) => s.homeAfterWaterFallStartIndex);

  return (
    <>
      {listData.map((item, index) => {
        if (!item.component_id) return null;

        if (homeAfterWaterFallStartIndex !== null && index > homeAfterWaterFallStartIndex) {
          return null;
        }

        const Component = resolvedComponentMap[item.component_id];
        if (!Component) return null;

        const component = (
          <Component
            moduleIndex={index}
            indexData={item}
            homeListData={homeListData}
            isMobile={isMobile}
            onTrack={onTrack}
            onExpTrack={onExpTrack}
            onValidExpTrack={onValidExpTrack}
            onBtnTrack={onBtnTrack}
          />
        );
        const shouldRenderOnServer =
          serverRenderPolicy === 'all' || SEO_CONTENT_COMPONENT_IDS.has(item.component_id);

        return (
          <div
            key={item.id || `section-${index}`}
            className="index-box"
            data-component-id={item.component_id}
          >
            {shouldRenderOnServer ? component : <ClientOnly>{component}</ClientOnly>}
          </div>
        );
      })}
    </>
  );
}
